import React from "react";
import PropTypes from "prop-types";
import { Flex, Icon } from "orcs-design-system";
import { last } from "lodash";
import { AIMessage } from "../AIMessage";
import { AGENTCORE_NAMES } from "../consts";
import { PromptButton } from "../SuggestedPrompts";
import {
  extractFollowUpPrompts,
  stripFollowUpTags,
} from "../utils/extractFollowUpPrompts";
import MessageFeedback from "../MessageFeedback";
import GqlExpertResponse from "../GqlExpertResponse";
import { ToolBadge } from "./ToolBadge";
import { AIMessageWrapper } from "./AIMessageWrapper";

const isGqlExpertContent = (content) => {
  try {
    const parsed = JSON.parse(content);
    return !!parsed?.suggested_queries;
  } catch {
    return false;
  }
};

export const AssistantMessage = ({
  message,
  messages,
  idx,
  isComplete,
  selectedAgent,
  makeHtmlParsingOptions,
  handlePromptSelect,
  onSubmitMessageFeedback,
}) => {
  const { tools = [] } = message;
  const lastTool = last(tools);
  const isLastAssistant = !messages
    .slice(idx + 1)
    .some((m) => m.role === "assistant");
  const isDone = message.status === "done";
  const followUpPrompts =
    isLastAssistant && isDone ? extractFollowUpPrompts(message.content) : [];
  const displayContent = stripFollowUpTags(message.content);

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      mt="4px"
      mb="4px"
      gap="8px"
    >
      {lastTool && message.status !== "done" && (
        <AIMessageWrapper>
          <ToolBadge message={lastTool} allToolsCalled={tools} />
        </AIMessageWrapper>
      )}

      <AIMessageWrapper>
        {selectedAgent === AGENTCORE_NAMES.GQL_EXPERT_V2 ||
        isGqlExpertContent(displayContent) ? (
          <GqlExpertResponse content={displayContent || ""} />
        ) : (
          <AIMessage
            complete={isDone}
            content={displayContent || lastTool?.thought}
            options={makeHtmlParsingOptions(!isDone)}
            showAIWarning={false}
            ariaLabel="AI Response"
          />
        )}
        <Flex justifyContent="flex-end">
          <MessageFeedback
            messageId={message.id}
            initialRating={message.feedbackRating}
            initialComment={message.feedbackComment}
            onSubmitFeedback={onSubmitMessageFeedback}
            disabled={!isComplete}
          />
        </Flex>
      </AIMessageWrapper>

      {isComplete && followUpPrompts.length > 0 && (
        <Flex flexWrap="wrap" gap="xs">
          {followUpPrompts.map(({ label, prompt }) => (
            <PromptButton
              key={prompt}
              variant="ghost"
              small
              disabled={!isComplete}
              onClick={() => handlePromptSelect(prompt)}
            >
              {label}
              &nbsp;&nbsp;
              {prompt.includes("/dashboard") && (
                <Icon icon={["fa", "chart-line"]} color="subText" />
              )}
            </PromptButton>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

AssistantMessage.propTypes = {
  message: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  idx: PropTypes.number.isRequired,
  isComplete: PropTypes.bool.isRequired,
  selectedAgent: PropTypes.string.isRequired,
  makeHtmlParsingOptions: PropTypes.func.isRequired,
  handlePromptSelect: PropTypes.func.isRequired,
  onSubmitMessageFeedback: PropTypes.func.isRequired,
};
